import { REVIEWER_GROUPS, ReviewerGroup } from '@/modules/performance/hr-review-core';
import { CategoryAverage, QualitativeResult } from './qualitative';

/** Defaults the legacy backend served until an admin saved a KpiConfiguration (KpiConfigurationService.CreateDefaults). */
export const DEFAULT_SCORING_CONFIG = {
  qnMatrixMinLevel: 60,
  qnMatrixMaxLevel: 110,
  normalizedMaxScale: 4,
  reviewerGroupWeights: {
    'Senior Peer': 0.2,
    'Equal Peer': 0.15,
    'Junior Peer': 0.15,
    'Other Peer': 0.1,
    Self: 0.1,
    Head: 0.3,
  } as Record<string, number>,
  categoryWeights: {
    'Work Output': 0.2,
    Communication: 0.15,
    Culture: 0.15,
    'Improvement Initiative': 0.1,
    'AI and Frontier Tech': 0.15,
    Leadership: 0.15,
    'Skills Assessment': 0.1,
  } as Record<string, number>,
};

export type ScoringConfig = typeof DEFAULT_SCORING_CONFIG;

const QUALITATIVE_WEIGHT = 0.75;
const QUANTITATIVE_WEIGHT = 0.25;
const PERFORMANCE_AXIS_QUANTITATIVE_WEIGHT = 0.6;
const PERFORMANCE_AXIS_CATEGORY_WEIGHT = 0.4;
export const STRENGTH_CATEGORY_THRESHOLD = 3.0;
export const IMPROVEMENT_CATEGORY_THRESHOLD = 2.75;

export type AxisTag = 'performance' | 'potential';

/** Which axis of the nine-box each topic feeds (legacy PERFORMANCE_DASHBOARD_CATEGORIES). */
const CATEGORY_AXIS: Record<string, AxisTag> = {
  'Work Output': 'performance',
  Communication: 'performance',
  'Skills Assessment': 'performance',
  Culture: 'potential',
  'Improvement Initiative': 'potential',
  'AI and Frontier Tech': 'potential',
  Leadership: 'potential',
};

/** Customer-project ids the legacy dashboard leaves out of quantitative scoring (internal "General Ops" projects). */
const EXCLUDED_QUANTITATIVE_PROJECT_IDS: ReadonlySet<string> = new Set([
  '414339794963857409',
  '413177133812613121',
  '413336405375057921',
  '382154725421875202',
  '366483513396035586',
  '428428926911119361',
  '413157502117478401',
  '413318261469872129',
  '477122424894849025',
  '413303764932165633',
]);

const RATING_BAND_THRESHOLDS = { OUTSTANDING: 8.5, EXCEEDS_EXPECTATIONS: 7.0, MEETS_EXPECTATIONS: 5.5, PARTIALLY_MEETS: 4.0 };

export const getRatingBandLabel = (finalScore: number): string => {
  if (finalScore >= RATING_BAND_THRESHOLDS.OUTSTANDING) return 'DASHBOARD_RATING_OUTSTANDING';
  if (finalScore >= RATING_BAND_THRESHOLDS.EXCEEDS_EXPECTATIONS) return 'DASHBOARD_RATING_EXCEEDS_EXPECTATIONS';
  if (finalScore >= RATING_BAND_THRESHOLDS.MEETS_EXPECTATIONS) return 'DASHBOARD_RATING_MEETS_EXPECTATIONS';
  if (finalScore >= RATING_BAND_THRESHOLDS.PARTIALLY_MEETS) return 'DASHBOARD_RATING_PARTIALLY_MEETS';
  return 'DASHBOARD_RATING_BELOW_EXPECTATIONS';
};

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

/**
 * A reviewer group nobody in it submitted for gives up its weight, shared equally among the groups that did submit -
 * otherwise a missing group would silently drag every score down.
 */
export const computeEffectiveGroupWeights = (
  weights: Record<string, number>,
  submittedByGroup: Record<ReviewerGroup, number>
): Record<ReviewerGroup, number> => {
  const submitted = REVIEWER_GROUPS.filter((group) => submittedByGroup[group] > 0);
  const unclaimed = REVIEWER_GROUPS.filter((group) => submittedByGroup[group] === 0).reduce((sum, group) => sum + (weights[group] ?? 0), 0);
  const share = submitted.length ? unclaimed / submitted.length : 0;
  return Object.fromEntries(
    REVIEWER_GROUPS.map((group) => [group, submittedByGroup[group] > 0 ? (weights[group] ?? 0) + share : 0])
  ) as Record<ReviewerGroup, number>;
};

export interface CategoryScoreResult {
  category: string;
  weight: number;
  /** Weighted average across reviewer groups, on the normalized scale (out of 4 by default). */
  weightedAverage: number;
  /** Highest minus lowest group score: a large spread means reviewers disagree. */
  spread: number;
  byGroup: Record<ReviewerGroup, number>;
}

export interface QualitativeScoring {
  categories: CategoryScoreResult[];
  effectiveGroupWeights: Record<ReviewerGroup, number>;
  /** Out of 10. */
  score: number;
  reviewerGroupsSubmitted: number;
  reviewerGroupsTotal: number;
  isComplete: boolean;
}

export const scoreQualitative = (result: QualitativeResult, config: ScoringConfig): QualitativeScoring => {
  const effectiveGroupWeights = computeEffectiveGroupWeights(config.reviewerGroupWeights, result.submittedByGroup);

  const categories = result.categoryAverages.map((average: CategoryAverage): CategoryScoreResult => {
    const values = REVIEWER_GROUPS.map((group) => average.AverageByReviewerGroup[group] ?? 0);
    return {
      category: average.Category,
      weight: config.categoryWeights[average.Category] ?? 0,
      weightedAverage: REVIEWER_GROUPS.reduce((sum, group) => sum + (average.AverageByReviewerGroup[group] ?? 0) * effectiveGroupWeights[group], 0),
      spread: Math.max(...values) - Math.min(...values),
      byGroup: average.AverageByReviewerGroup,
    };
  });

  const outOfMax = categories.reduce((sum, category) => sum + category.weightedAverage * category.weight, 0);
  const reviewerGroupsTotal = REVIEWER_GROUPS.length;

  return {
    categories,
    effectiveGroupWeights,
    score: (outOfMax / config.normalizedMaxScale) * 10,
    reviewerGroupsSubmitted: result.totalGroups,
    reviewerGroupsTotal,
    isComplete: result.totalGroups >= reviewerGroupsTotal,
  };
};

export interface QuantitativeRow {
  ProjectId: string;
  ProjectName?: string;
  CustomerName?: string;
  ThisYearYTDRevenue: number;
  ThisYearTgtRevenue: number;
  LastYearTgtRevenue: number;
  LastYearYTDRevenue: number;
  ThisYearYTDGP: number;
  ThisYearTgtGP: number;
  ThisYearYTDNP: number;
  ThisYearTgtNP: number;
  ThisYearYTDNPMargin: number;
  ThisYearTgtNPMargin: number;
  ThisYearYTDNPS: number;
  ThisYearTgtNPS: number;
  ThisYearYTDRevenueGrowth: number;
  ThisYearTgtRevenueGrowth: number;
}

interface MetricScore {
  key: string;
  label: string;
  target: number;
  actual: number;
  achievementPercent: number;
  /** Out of 10. */
  score: number;
}

export interface QuantitativeScoring {
  metrics: MetricScore[];
  /** Out of 10; 0 when the employee has no eligible project. */
  score: number;
  isEligible: boolean;
  revenueActual: number;
  projectCount: number;
}

const achievement = (actual: number, target: number) => (target !== 0 ? (actual / target) * 100 : 0);
const sum = (rows: QuantitativeRow[], pick: (row: QuantitativeRow) => number) => rows.reduce((total, row) => total + pick(row), 0);

/** Maps an achievement percentage onto 0-10 between the configured floor (0 points) and cap (10 points). */
export const achievementToScore = (achievementPercent: number, floorPercent: number, capPercent: number) =>
  clamp(((achievementPercent - floorPercent) / (capPercent - floorPercent)) * 10, 0, 10);

export const scoreQuantitative = (rows: QuantitativeRow[], config: ScoringConfig): QuantitativeScoring => {
  const projects = rows.filter((row) => !EXCLUDED_QUANTITATIVE_PROJECT_IDS.has(row.ProjectId));
  if (projects.length === 0) return { metrics: [], score: 0, isEligible: false, revenueActual: 0, projectCount: 0 };

  const revenue = { actual: sum(projects, (r) => r.ThisYearYTDRevenue), target: sum(projects, (r) => r.ThisYearTgtRevenue) };
  const grossProfit = { actual: sum(projects, (r) => r.ThisYearYTDGP), target: sum(projects, (r) => r.ThisYearTgtGP) };
  const netProfit = { actual: sum(projects, (r) => r.ThisYearYTDNP), target: sum(projects, (r) => r.ThisYearTgtNP) };
  const lastYearRevenue = { actual: sum(projects, (r) => r.LastYearYTDRevenue), target: sum(projects, (r) => r.LastYearTgtRevenue) };

  const margin = {
    actual: revenue.actual !== 0 ? (netProfit.actual / revenue.actual) * 100 : 0,
    target: revenue.target !== 0 ? (netProfit.target / revenue.target) * 100 : 0,
  };
  const growth = {
    actual: lastYearRevenue.actual !== 0 ? ((revenue.actual - lastYearRevenue.actual) / lastYearRevenue.actual) * 100 : 0,
    target: lastYearRevenue.target !== 0 ? ((revenue.target - lastYearRevenue.target) / lastYearRevenue.target) * 100 : 0,
  };
  // NPS is a revenue-weighted average of the projects' own scores.
  const revenueBasis = revenue.actual;
  const weightOf = (row: QuantitativeRow) => (revenueBasis !== 0 ? row.ThisYearYTDRevenue / revenueBasis : 0);
  const nps = {
    actual: projects.reduce((total, row) => total + row.ThisYearYTDNPS * weightOf(row), 0),
    target: projects.reduce((total, row) => total + row.ThisYearTgtNPS * weightOf(row), 0),
  };

  const definitions: { key: string; label: string; value: { actual: number; target: number } }[] = [
    { key: 'revenue', label: 'Revenue', value: revenue },
    { key: 'grossProfit', label: 'Gross profit', value: grossProfit },
    { key: 'netProfit', label: 'Net profit', value: netProfit },
    { key: 'npMargin', label: 'NP margin', value: margin },
    { key: 'nps', label: 'NPS (1-10)', value: nps },
    { key: 'yoyGrowth', label: 'YoY rev growth', value: growth },
  ];

  const metrics = definitions.map(({ key, label, value }): MetricScore => {
    const achievementPercent = achievement(value.actual, value.target);
    return {
      key,
      label,
      target: value.target,
      actual: value.actual,
      achievementPercent,
      score: achievementToScore(achievementPercent, config.qnMatrixMinLevel, config.qnMatrixMaxLevel),
    };
  });

  return {
    metrics,
    score: metrics.reduce((total, metric) => total + metric.score / metrics.length, 0),
    isEligible: true,
    revenueActual: revenue.actual,
    projectCount: projects.length,
  };
};

const axisAverage = (categories: CategoryScoreResult[], tag: AxisTag, normalizedMax: number) => {
  const tagged = categories.filter((category) => CATEGORY_AXIS[category.category] === tag);
  const totalWeight = tagged.reduce((total, category) => total + category.weight, 0);
  if (!totalWeight) return 0;
  return tagged.reduce((total, category) => total + (category.weightedAverage / normalizedMax) * 10 * category.weight, 0) / totalWeight;
};

/** Performance blends financial results (60%) with the performance topics (40%); without projects it is topics only. */
export const getPerformanceAxis = (
  categories: CategoryScoreResult[],
  quantitativeScore: number,
  isQuantitativeEligible: boolean,
  normalizedMax: number
) => {
  const topics = axisAverage(categories, 'performance', normalizedMax);
  return isQuantitativeEligible
    ? PERFORMANCE_AXIS_QUANTITATIVE_WEIGHT * quantitativeScore + PERFORMANCE_AXIS_CATEGORY_WEIGHT * topics
    : topics;
};

export const getPotentialAxis = (categories: CategoryScoreResult[], normalizedMax: number) =>
  axisAverage(categories, 'potential', normalizedMax);

/** 75% qualitative + 25% quantitative; without eligible projects the qualitative score stands alone. */
export const getFinalScore = (qualitativeScore: number, quantitativeScore: number, isQuantitativeEligible: boolean) =>
  isQuantitativeEligible ? qualitativeScore * QUALITATIVE_WEIGHT + quantitativeScore * QUANTITATIVE_WEIGHT : qualitativeScore;
